/**
 * Dashboard statistics data fetching hook.
 * 
 * @module hooks/useDashboardStats
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { isExcludedUser } from '../config/excluded-users';
import type { ExclusionRules } from './useExcludedUsers';
import type { DashboardStats, ExcludedUser, FullReport, Plan } from '../types';

/** Plans excluded from the "Users by Plan" dashboard widget. */
const HIDDEN_PLAN_NAMES = new Set(['leads', 'internal team']);

/** Display order for the "Users by Plan" dashboard widget. Unlisted plans sort to the end. */
const PLAN_DISPLAY_ORDER = ['Automated', 'Agency', 'Pro', 'Free trials'];

/** Plan name remapping for display (matched case-insensitively). */
const PLAN_NAME_REMAP: Record<string, string> = { 'no plan': 'Free trials' };

/**
 * Fetches dashboard overview statistics.
 * 
 * @returns Query result with dashboard stats
 */
export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async (): Promise<DashboardStats> => {
      // Fetch counts and exclusion rules in parallel.
      // NOTE: The users query fetches all rows (no .limit()) so we can split
      // internal vs external users client-side. Revisit if the user table grows
      // beyond a few thousand rows.
      const [usersResult, businessesResult, reportsResult, plansResult, rulesResult] = await Promise.all([
        supabase.from('users').select('id, email, subscription_status, plan_id'),
        supabase.from('businesses').select('id, users!businesses_profile_id_fkey(email)'),
        supabase.from('full_reports').select('id, users!profile_id(email)'),
        supabase.from('plans').select('*'),
        supabase.from('excluded_users').select('*'),
      ]);

      if (usersResult.error) throw new Error(`Failed to fetch users: ${usersResult.error.message}`);
      if (businessesResult.error) throw new Error(`Failed to fetch businesses: ${businessesResult.error.message}`);
      if (reportsResult.error) throw new Error(`Failed to fetch reports: ${reportsResult.error.message}`);
      if (plansResult.error) throw new Error(`Failed to fetch plans: ${plansResult.error.message}`);
      if (rulesResult.error) throw new Error(`Failed to fetch exclusion rules: ${rulesResult.error.message}`);

      // Build exclusion rules from database
      const rulesData = (rulesResult.data || []) as unknown as ExcludedUser[];
      const rules: ExclusionRules = { domains: [], emails: [] };
      for (const row of rulesData) {
        if (row.type === 'domain') rules.domains.push(row.value.toLowerCase());
        else if (row.type === 'email') rules.emails.push(row.value.toLowerCase());
      }

      // Partition users into external (real) and excluded (internal) in a single pass
      type UserRow = { id: string; email: string; subscription_status: string | null; plan_id: string | null };
      const allUsers = (usersResult.data as UserRow[] | null) || [];
      const excludedUsers: UserRow[] = [];
      const externalUsers: UserRow[] = [];
      for (const u of allUsers) {
        (isExcludedUser(u.email, rules) ? excludedUsers : externalUsers).push(u);
      }
      const excludedCount = excludedUsers.length;

      // Partition businesses into external and excluded using owner email
      type BusinessRow = { id: string; users: { email: string } | null };
      const allBusinesses = (businessesResult.data as BusinessRow[] | null) || [];
      const excludedBusinessList: BusinessRow[] = [];
      const externalBusinessList: BusinessRow[] = [];
      for (const b of allBusinesses) {
        (isExcludedUser(b.users?.email, rules) ? excludedBusinessList : externalBusinessList).push(b);
      }

      // Partition reports into external and excluded using owner email
      type ReportRow = { id: string; users: { email: string } | null };
      const allReports = (reportsResult.data as ReportRow[] | null) || [];
      const excludedReportsList: ReportRow[] = [];
      const externalReportsList: ReportRow[] = [];
      for (const r of allReports) {
        (isExcludedUser(r.users?.email, rules) ? excludedReportsList : externalReportsList).push(r);
      }

      // Calculate paid users: active subscription AND on a paid plan.
      // Trialing users are excluded — they have not yet converted to a paid subscription.
      const plans = (plansResult.data as Plan[]) || [];
      const paidPlanIds = new Set(plans.filter((p) => p.paid_plan).map((p) => p.id));
      const isPaidUser = (u: UserRow) =>
        u.subscription_status === 'active'
        && u.plan_id !== null && paidPlanIds.has(u.plan_id);

      const paidUsers = externalUsers.filter(isPaidUser).length;
      const excludedPaidUsers = excludedUsers.filter(isPaidUser).length;

      // Calculate plan distribution (external only)
      // Filter out internal-only plans, remap names, enforce display order
      const planDistribution = plans
        .filter((plan) => !HIDDEN_PLAN_NAMES.has(plan.name.toLowerCase()))
        .map((plan) => ({
          planId: plan.id,
          planName: PLAN_NAME_REMAP[plan.name.toLowerCase()] ?? plan.name,
          userCount: externalUsers.filter((u) => u.plan_id === plan.id).length,
        }))
        .sort((a, b) => {
          const ai = PLAN_DISPLAY_ORDER.indexOf(a.planName);
          const bi = PLAN_DISPLAY_ORDER.indexOf(b.planName);
          return (ai === -1 ? PLAN_DISPLAY_ORDER.length : ai) - (bi === -1 ? PLAN_DISPLAY_ORDER.length : bi);
        });

      return {
        totalUsers: externalUsers.length,
        totalBusinesses: externalBusinessList.length,
        totalReports: externalReportsList.length,
        paidUsers,
        recentReports: 0,
        planDistribution,
        excludedUsers: excludedCount,
        excludedPaidUsers,
        excludedBusinesses: excludedBusinessList.length,
        excludedReports: excludedReportsList.length,
      };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes - refresh more frequently for dashboard
  });
}

/**
 * Fetches recent reports for dashboard.
 * 
 * @param limit - Number of reports to fetch
 * @returns Query result with recent reports
 */
export function useRecentReports(limit = 10) {
  return useQuery({
    queryKey: ['recent-reports', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('full_reports')
        .select(
          '*, businesses!business_id(id, business_name), users!profile_id(id, email, full_name)'
        )
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw new Error(`Failed to fetch recent reports: ${error.message}`);
      }

      return (data as (FullReport & { 
        businesses: { id: string; business_name: string } | null;
        users: { id: string; email: string; full_name: string | null } | null;
      })[]) || [];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Fetches plans for filter dropdowns.
 * 
 * @returns Query result with plans
 */
export function usePlans() {
  return useQuery({
    queryKey: ['plans'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .order('name');

      if (error) {
        throw new Error(`Failed to fetch plans: ${error.message}`);
      }

      return (data as Plan[]) || [];
    },
  });
}
